import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { childKeyOf } from '../models/childKeys';
import { TgvActions } from '../tgvActions';
import { markAndDeleteKeys } from './markAndDeleteKeys';

/** Everything that can make an already created offer unnecessary. */
export const OFFER_RELEVANT_ACTIONS = [
  TgvActions.TGV_TIER_REMOVED,
  TgvActions.TGV_UMFANG_CHANGED,
  TgvActions.TGV_TIERART_SELECTED,
  TgvActions.TGV_JURISTISCH_RECEIVED,
];

export function* watchOrphanedOffers(): SagaIterator {
  yield takeEvery(OFFER_RELEVANT_ACTIONS, safeSaga(deferOrphanedOffersSaga, App.TGV));
}

export const findOrphanKeys = (
  { tiergruppe, transientState }: IAppState,
  additionalKeys: string[] = [],
): string[] => {
  const neededTkv = new Set(
    tiergruppe.tiere
      .filter(tier => tier.tierart !== null && tier.umfang.tkv)
      .map(tier => tier.cardId),
  );
  const candidates = [
    ...Object.entries(tiergruppe.angebotsIds)
      .filter(([cardId]) => !neededTkv.has(cardId))
      .map(([, angebotsId]) => ({ appId: App.TKV_V2, angebotsId })),
    ...(tiergruppe.thhAngelegt.thhHund && tiergruppe.positions.thhHund === null
      ? [{ appId: App.THH_HUND }]
      : []),
    ...(tiergruppe.thhAngelegt.thhPferd && tiergruppe.positions.thhPferd === null
      ? [{ appId: App.THH_PFERD }]
      : []),
  ];
  return [...new Set([...candidates.map(child => childKeyOf(child)), ...additionalKeys])].filter(
    (key): key is string =>
      key !== null &&
      !transientState.children.creating.includes(key) &&
      !transientState.children.pendingDeletion.includes(key),
  );
};

export function* deferOrphanedOffersSaga(): SagaIterator {
  const state: IAppState = yield select();
  yield put(new TgvActions().orphansDeferred(findOrphanKeys(state)));
}

/**
 * Offers are created on "Zum Angebot". When the user then removes an animal or drops a
 * coverage, the offer must disappear from the Warenkorb like a deleted product when the take-over
 * of the group starts or a cancelled round was restored: PRODUKT_DELETED is the command of the
 * dashboard as well. The reducers of this app, the shop and the product apps clean up on that
 * event.
 */
export function* removeOrphanedOffersSaga(): SagaIterator {
  yield* removeOrphanedOffersAndKeys([]);
}

export function* removeOrphanedOffersAndKeys(additionalKeys: string[]): SagaIterator<boolean> {
  const state: IAppState = yield select();
  const orphanedKeys = findOrphanKeys(state, additionalKeys);
  if (orphanedKeys.length > 0) {
    yield* markAndDeleteKeys(orphanedKeys);
  }
  return orphanedKeys.length > 0;
}
